import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Code,
  HStack,
  IconButton,
  Text,
  Tooltip,
  Heading,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import { useState, useMemo, useCallback } from 'react'
import { FiEye } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ColumnConfig, FilterConfig } from '@/components/common/DataTable/types'
import { DataTable } from '@/components/common/DataTable'
import { useEnqueueSettlementVerification, useSettlementRecords } from '@/hooks/useAdminData'
import { SettlementRecord } from '@/types'
import { formatDateTime, getExplorerTxUrl, truncateHash, sanitizeTokenSymbol } from '@/utils/formatUtils'
import { formatTokenAmount } from '@/utils/amountUtils'
import { ChainBadge } from '@/components/common/ChainBadge'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import { StatusBadge } from '@/components/common/StatusBadge'
import { TokenAmountDisplay } from '@/components/common/TokenAmountDisplay'
import { useToast } from '@/hooks/useToast'
import { getChainPrefix } from '@/constants/chains'
import SettlementRecordDetailModal from './SettlementRecordDetailModal'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { settlementService } from '@/services/admin/settlementService'

const STATUSES = [
  { value: 'srs_pending', labelKey: 'settlements.table.status.pending' },
  { value: 'srs_settled', labelKey: 'settlements.table.status.settled' },
  { value: 'srs_failed', labelKey: 'settlements.table.status.failed' },
]

const coalesceRecordField = (
  record: SettlementRecord,
  keys: readonly string[],
): unknown => {
  const source = record as unknown as Record<string, unknown>
  for (const key of keys) {
    if (key === undefined || key === null) {
      continue
    }
    const value = source[key]
    if (value !== undefined && value !== null) {
      return value
    }
  }
  return undefined
}

const getRecordString = (
  record: SettlementRecord,
  keys: readonly string[],
): string | undefined => {
  const value = coalesceRecordField(record, keys)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  return undefined
}

const getRecordNumber = (
  record: SettlementRecord,
  keys: readonly string[],
): number | undefined => {
  const value = coalesceRecordField(record, keys)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const getRecordBoolean = (
  record: SettlementRecord,
  keys: readonly string[],
): boolean | undefined => {
  const value = coalesceRecordField(record, keys)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false
    }
  }
  return undefined
}

const splitOrderId = (id?: string | null): string[] => {
  if (!id) return []
  return id
    .split('-')
    .map(segment => segment.trim())
    .filter(Boolean)
}

export function SettlementRecordsTable() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const cardBg = useColorModeValue('white', 'gray.800')
  const subtitleColor = useColorModeValue('gray.600', 'gray.300')
  const copyableCodeStyles = useCopyableCodeStyles()

  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [filters, setFilters] = useState<{ status?: string; orderId?: string }>({})
  const [selectedRecord, setSelectedRecord] = useState<SettlementRecord | null>(null)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { success: showToastSuccess, error: showToastError, warning: showToastWarning } = useToast()
  const enqueueVerification = useEnqueueSettlementVerification()

  const { data, isLoading, error, refetch, isFetching } = useSettlementRecords({
    page,
    limit,
    status: filters.status,
    order_id: filters.orderId,
  })

  const guard = data?.guard
  const queueConfigured = guard?.verificationQueueConfigured ?? false
  const manualAllowed = guard?.verificationManualAllowed ?? false
  const records = data?.data ?? []

  const getChainIdentifier = useCallback((record: SettlementRecord) => {
    const identifier =
      getRecordString(record, ['chainCode', 'chain_code', 'chainId', 'chain_id']) ??
      getRecordNumber(record, ['chainNumericId', 'chain_numeric_id'])
    return identifier !== undefined ? String(identifier) : 'unknown'
  }, [])

  const isSolanaRecord = useCallback((record: SettlementRecord) => {
    return getChainPrefix(getChainIdentifier(record)) === 'solana'
  }, [getChainIdentifier])

  const formatAmountDisplay = useCallback(
    (value: string | number | null | undefined, record: SettlementRecord) => {
      const symbol = sanitizeTokenSymbol(
        getRecordString(record, ['tokenSymbol', 'token_symbol'])
      )
      const decimals = getRecordNumber(record, ['tokenDecimals', 'token_decimals'])
      const rawValue = (() => {
        if (value === null || value === undefined) {
          return (
            getRecordString(record, [
              'totalAmountInSmallestUnit',
              'total_amount_in_smallest_unit',
              'merchantAmountInSmallestUnit',
              'merchant_amount_in_smallest_unit',
              'platformFeeInSmallestUnit',
              'platform_fee_in_smallest_unit',
            ]) ?? '0'
          )
        }
        return typeof value === 'number' ? value.toString() : value
      })()

      if (decimals === undefined) {
        const numeric = Number(rawValue)
        const formatted = Number.isFinite(numeric)
          ? numeric.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })
          : rawValue
        return `${formatted} ${symbol}`.trim()
      }

      try {
        const formatted = formatTokenAmount(rawValue, decimals, 2)
        const numeric = Number(formatted)
        const display = Number.isFinite(numeric)
          ? numeric.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })
          : formatted
        return `${display} ${symbol}`.trim()
      } catch (error) {
        console.warn('Failed to format settlement amount; falling back to raw value.', {
          value,
          record,
          error,
        })
        return `${rawValue} ${symbol}`.trim()
      }
    },
    [getChainIdentifier]
  )

  const formatGasUsed = useCallback((value?: string | number | null) => {
    if (!value) {
      return '—'
    }
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return String(value)
    }
    return Math.round(numeric).toLocaleString()
  }, [])

  const formatGasPriceDisplay = useCallback((value?: string | number | null) => {
    if (!value) {
      return '—'
    }
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return String(value)
    }
    return `${(numeric / 1e9).toFixed(2)} Gwei`
  }, [])

  const handleCopy = useCallback(async (value: string, label?: string) => {
    try {
      if (typeof window === 'undefined' || !window.isSecureContext || !navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable in this browser or context.')
      }
      await navigator.clipboard.writeText(value)

      showToastSuccess({
        title: t('common.copySuccess'),
        description: label ?? value,
      })
    } catch (error: any) {
      showToastError({
        title: t('common.copyFailed'),
        description: error?.message || label || value,
      })
    }
  }, [showToastError, showToastSuccess, t])

  const handleCloseModal = useCallback(() => {
    onClose()
    setSelectedRecord(null)
  }, [onClose])

  const handleOpenModal = useCallback((record: SettlementRecord) => {
    setSelectedRecord(record)
    onOpen()
  }, [onOpen])

  const handleRefreshSelectedRecord = useCallback(async () => {
    const selectedId = selectedRecord?.id
    const result = await refetch()
    if (!selectedId) {
      return
    }
    const refreshedRecord = result.data?.data?.find(
      record => record.id === selectedId
    )
    if (refreshedRecord) {
      setSelectedRecord(refreshedRecord)
    }
  }, [refetch, selectedRecord?.id])

  const handleFiltersChange = useCallback((newFilters: Record<string, any>) => {
    setFilters({
      status: newFilters.status,
      orderId: newFilters.orderId,
    })
    setPage(1)
  }, [])

  const tableColumns = useMemo<ColumnConfig<SettlementRecord>[]>(() => [
    {
      key: 'orderId',
      title: t('settlements.table.columns.orderId'),
      render: (_, record) => {
        const primaryId = getRecordString(record, ['orderId', 'order_id']) ?? '—'
        const businessId = getRecordString(record, ['orderBusinessId', 'order_business_id'])
        const segments = splitOrderId(primaryId)
        return (
          <VStack align="flex-start" spacing={0}>
            <Tooltip label={t('common.copy')}>
              <Box minH="44px" display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start">
                <Code
                  as="div"
                  fontSize="xs"
                  bg="transparent"
                  cursor="pointer"
                  wordBreak="break-word"
                  whiteSpace="pre-line"
                  _hover={copyableCodeStyles.hover}
                  _active={copyableCodeStyles.active}
                  onClick={() => handleCopy(primaryId, t('settlements.table.columns.orderId'))}
                >
                  {segments.length > 0 ? segments.join('\n') : primaryId}
                </Code>
              </Box>
            </Tooltip>
            {businessId && (
              <Text fontSize="xs" color={subtitleColor} noOfLines={1}>
                {businessId}
              </Text>
            )}
          </VStack>
        )
      },
    },
    {
      key: 'chainId',
      title: t('settlements.table.columns.chain'),
      render: (_, record) => (
        <ChainBadge chainId={getChainIdentifier(record)} fontSize="10px" px={1.5} py={0.5} />
      ),
    },
    {
      key: 'sweepSeq',
      title: t('settlements.table.columns.sequence'),
      render: (_, record) => {
        const sequence = getRecordNumber(record, ['sweepSeq', 'sweep_seq'])
        return <Text fontSize="xs">{sequence ?? '—'}</Text>
      },
    },
    {
      key: 'totalAmountInSmallestUnit',
      title: t('settlements.table.columns.totalAmount'),
      render: (_, record) => (
        <TokenAmountDisplay
          value={formatAmountDisplay(
            getRecordString(record, ['totalAmountInSmallestUnit', 'total_amount_in_smallest_unit'])
              ?? '0',
            record
          )}
        />
      ),
    },
    {
      key: 'merchantAmountInSmallestUnit',
      title: t('settlements.table.columns.merchantAmount'),
      render: (_, record) => (
        <TokenAmountDisplay
          value={formatAmountDisplay(
            getRecordString(record, ['merchantAmountInSmallestUnit', 'merchant_amount_in_smallest_unit'])
              ?? '0',
            record
          )}
        />
      ),
    },
    {
      key: 'platformFeeInSmallestUnit',
      title: t('settlements.table.columns.platformFee'),
      render: (_, record) => (
        <TokenAmountDisplay
          value={formatAmountDisplay(
            getRecordString(record, ['platformFeeInSmallestUnit', 'platform_fee_in_smallest_unit'])
              ?? '0',
            record
          )}
        />
      ),
    },
    {
      key: 'statusId',
      title: t('settlements.table.columns.status'),
      render: (_, record) => {
        const statusId = getRecordString(record, ['statusId', 'status_id']) ?? 'unknown'
        return (
          <StatusBadge
            statusType="settlement"
            status={statusId}
            label={t(`settlements.table.statusLabel.${statusId}`, statusId)}
          />
        )
      },
    },
    {
      key: 'isVerified',
      title: t('settlements.table.columns.isVerified'),
      render: (_, record) => {
        const verified = getRecordBoolean(record, ['isVerified', 'is_verified']) ?? false
        return (
          <Badge colorScheme={verified ? 'green' : 'gray'}>
            {verified ? t('common.yes') : t('common.no')}
          </Badge>
        )
      },
    },
    {
      key: 'transactionHash',
      title: t('settlements.table.columns.transactionHash'),
      render: (_, record) => {
        const txHash = getRecordString(record, ['transactionHash', 'transaction_hash'])
        if (!txHash) {
          return <Text fontSize="xs">—</Text>
        }

        const explorerUrl = getExplorerTxUrl(getChainIdentifier(record), txHash)

        return (
          <HStack spacing={1} align="center">
            <Text fontFamily="mono" fontSize="xs" noOfLines={1}>
              {truncateHash(txHash, 8, 6)}
            </Text>
            <Tooltip label={t('common.copy')}>
              <IconButton
                aria-label={t('settlements.table.actions.copyHash')}
                icon={<CopyIcon boxSize={3} />}
                size="xs"
                variant="ghost"
                onClick={() => handleCopy(txHash, t('settlements.table.columns.transactionHash'))}
              />
            </Tooltip>
            {explorerUrl && (
              <Tooltip label={t('common.openExplorer')}>
                <IconButton
                  aria-label={t('settlements.table.actions.openExplorer')}
                  icon={<ExternalLinkIcon boxSize={3} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                    }
                  }}
                />
              </Tooltip>
            )}
          </HStack>
        )
      },
    },
    {
      key: 'createdAt',
      title: t('settlements.table.columns.createdAt'),
      render: (_, record) => {
        const createdAt = getRecordString(record, ['createdAt', 'created_at'])
        return (
          <Text fontSize="xs">
            {createdAt ? formatDateTime(createdAt) : '—'}
          </Text>
        )
      },
    },
    {
      key: 'updatedAt',
      title: t('settlements.table.columns.updatedAt'),
      render: (_, record) => {
        const updatedAt = getRecordString(record, ['updatedAt', 'updated_at'])
        return (
          <Text fontSize="xs">
            {updatedAt ? formatDateTime(updatedAt) : '—'}
          </Text>
        )
      },
    },
    {
      key: 'actions',
      title: t('settlements.table.columns.actions'),
      render: (_, record) => (
        <Tooltip label={t('settlements.table.actions.viewDetails')}>
          <IconButton
            aria-label={t('settlements.table.actions.viewDetails')}
            icon={<FiEye />}
            size="sm"
            variant="ghost"
            onClick={() => handleOpenModal(record)}
          />
        </Tooltip>
      ),
    },
  ], [
    formatAmountDisplay,
    getChainIdentifier,
    handleCopy,
    handleOpenModal,
    subtitleColor,
    t,
  ])

  const filterConfigs = useMemo<FilterConfig[]>(() => [
    {
      key: 'orderId',
      type: 'text',
      label: t('settlements.table.filters.orderId'),
      placeholder: t('settlements.table.filters.orderPlaceholder'),
      width: '260px',
    },
    {
      key: 'status',
      type: 'select',
      label: t('settlements.table.filters.status'),
      placeholder: t('settlements.table.filters.statusPlaceholder'),
      width: '200px',
      options: STATUSES.map(status => ({
        value: status.value,
        label: t(status.labelKey),
      })),
    },
  ], [t])

  const pagination = data?.pagination
    ? {
        page: data.pagination.page,
        pageSize: data.pagination.limit,
        total: data.pagination.total,
        pageSizeOptions: [10, 20, 50, 100],
      }
    : undefined

  const handleConfirmVerification = useCallback(async () => {
    if (!selectedRecord) {
      return
    }

    if (!queueConfigured) {
      showToastWarning({
        title: t('settlements.verification.queueMissingTitle'),
        description: t('settlements.verification.queueMissingDescription'),
      })
      return
    }

    if (!manualAllowed) {
      showToastWarning({
        title: t('settlements.verification.manualDisabledTitle'),
        description: t('settlements.verification.manualDisabledDescription', { mode: guard?.mode ?? 'manual' }),
      })
      return
    }

    try {
      const orderId = getRecordString(selectedRecord, ['orderBusinessId', 'order_id', 'orderId'])
      if (!orderId) {
        showToastError({
          title: t('settlements.verification.errorTitle'),
          description: t('settlements.verification.errorDescription'),
        })
        return
      }

      await enqueueVerification.mutateAsync({ orderIds: [orderId] })
      showToastSuccess({ title: t('settlements.verification.singleSuccess') })
      handleCloseModal()
      await refetch()
    } catch (error: any) {
      showToastError({
        title: t('settlements.verification.errorTitle'),
        description: error?.message || t('settlements.verification.errorDescription'),
      })
    }
  }, [enqueueVerification, guard?.mode, handleCloseModal, manualAllowed, queueConfigured, refetch, selectedRecord, showToastError, showToastSuccess, showToastWarning, t])

  const handleExport = useCallback(async () => {
    try {
      const { blob, filename } = await settlementService.exportCsv({
        status: filters.status,
        order_id: filters.orderId,
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      showToastSuccess({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      showToastError({
        title: t('common.exportCsv'),
        description: error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }, [currentEnv, filters.orderId, filters.status, showToastError, showToastSuccess, t])

  return (
    <VStack align="stretch" spacing={4}>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6} shadow="sm" bg={cardBg}>
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="md" mb={1}>
              {t('settlements.table.title')}
            </Heading>
            <Text fontSize="sm" color={subtitleColor}>
              {t('settlements.table.subtitle')}
            </Text>
          </Box>

          {!queueConfigured && (
            <Alert status="error" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>{t('settlements.verification.queueMissingTitle')}</AlertTitle>
                <AlertDescription>{t('settlements.verification.queueMissingDescription')}</AlertDescription>
              </Box>
            </Alert>
          )}

          {queueConfigured && !manualAllowed && (
            <Alert status="warning" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>{t('settlements.verification.manualDisabledTitle')}</AlertTitle>
                <AlertDescription>
                  {t('settlements.verification.manualDisabledDescription', { mode: guard?.mode ?? 'manual' })}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          <DataTable
            data={records}
            loading={isLoading}
            error={error}
            columns={tableColumns}
            filters={filterConfigs}
            onFiltersChange={handleFiltersChange}
            sortable
            showFilterBar
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showRefresh
            refreshing={isFetching}
            onRefresh={refetch}
            emptyMessage={t('settlements.table.empty')}
            showExport
            onExport={handleExport}
          />
        </VStack>
      </Box>

      <SettlementRecordDetailModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        selectedRecord={selectedRecord}
        onRefresh={handleRefreshSelectedRecord}
        isRefreshing={isFetching}
        subtitleColor={subtitleColor}
        queueConfigured={queueConfigured}
        manualAllowed={manualAllowed}
        isSubmitting={enqueueVerification.isPending}
        onConfirmVerification={handleConfirmVerification}
        getRecordString={getRecordString}
        getRecordNumber={getRecordNumber}
        getRecordBoolean={getRecordBoolean}
        getChainIdentifier={getChainIdentifier}
        isSolanaRecord={isSolanaRecord}
        formatAmountDisplay={formatAmountDisplay}
        formatGasUsed={formatGasUsed}
        formatGasPriceDisplay={formatGasPriceDisplay}
        handleCopy={handleCopy}
      />
    </VStack>
  )
}
